import axios from "axios";
import { API_BASE_PATH } from "../../environment";
import { toast } from "react-toastify";

interface UpdatePromotionPayload {
  designation_from: string;
  designation_to: string;
  promotion_date: string;
}

interface UpdatedPromotionResponse {
  employee_id: string;
  designation_from: string;
  designation_to: string;
  promotion_date: string;
  employee: {
    employee_id: string;
    username: string;
  };
}

// ====== Error Extractor ======
const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message;
};

/**
 * Updates a promotion record for a specific employee.
 * @param token - Authentication token
 * @param employeeId - The ID of the employee whose promotion is being updated
 * @param data - Payload containing updated promotion details
 * @returns Promise resolving to the updated promotion response
 * @throws Error if authentication fails or update operation fails
 */
export const updatePromotion = async (
  token: string | null,
  employeeId: string,
  data: UpdatePromotionPayload
): Promise<UpdatedPromotionResponse> => {
  if (!token) {
    console.error("No authentication token provided");
    throw new Error("Authentication token is missing");
  }

  try {
    const formData = new URLSearchParams();
    formData.append("designation_from", data.designation_from);
    formData.append("designation_to", data.designation_to);
    formData.append("promotion_date", data.promotion_date);

    const response = await axios.put<UpdatedPromotionResponse>(
      `${API_BASE_PATH}/promotions/${employeeId}`,
      formData,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    toast.success("Promotion updated successfully");
    return response.data;
  } catch (error: any) {
    console.error("Error updating promotion:", error);
    const msg = extractErrorMessage(error);
    toast.error(msg);
    throw error;
  }
};
